import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { formatApiErrorDetail } from '../utils/helpers';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import {
  TrendingUp,
  TrendingDown,
  DollarSign,
  FileText,
  Plus,
  Pencil,
  Trash2,
  Download,
  Paperclip,
} from 'lucide-react';

const DashboardPage = () => {
  const { user } = useAuth();
  const isAdmin = user?.role === 'ADMIN';
  const [quotes, setQuotes] = useState([]);
  const [income, setIncome] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [overview, setOverview] = useState({ total_income: 0, total_expenses: 0, net_profit: 0 });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const [editQuoteDialog, setEditQuoteDialog] = useState(false);
  const [selectedQuote, setSelectedQuote] = useState(null);
  const [quoteStatus, setQuoteStatus] = useState('');
  const [quotePrice, setQuotePrice] = useState('');

  const [addIncomeDialog, setAddIncomeDialog] = useState(false);
  const [incomeForm, setIncomeForm] = useState({ amount: '', description: '', date: new Date().toISOString().split('T')[0] });

  const [addExpenseDialog, setAddExpenseDialog] = useState(false);
  const [expenseForm, setExpenseForm] = useState({ amount: '', category: '', description: '', date: new Date().toISOString().split('T')[0] });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [quotesRes, incomeRes, expensesRes, overviewRes] = await Promise.all([
        axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/quotes`, { withCredentials: true }),
        axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/income`, { withCredentials: true }),
        axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/expenses`, { withCredentials: true }),
        axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/overview`, { withCredentials: true }),
      ]);
      setQuotes(quotesRes.data);
      setIncome(incomeRes.data);
      setExpenses(expensesRes.data);
      setOverview(overviewRes.data);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || 'Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateQuote = async () => {
    try {
      await axios.put(
        `${process.env.REACT_APP_BACKEND_URL}/api/quotes/${selectedQuote.id}`,
        { status: quoteStatus, price: quotePrice ? parseFloat(quotePrice) : null },
        { withCredentials: true }
      );
      setEditQuoteDialog(false);
      fetchData();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || 'Failed to update quote');
    }
  };

  const handleDeleteQuote = async (id) => {
    if (!window.confirm('Are you sure you want to delete this quote?')) return;
    try {
      await axios.delete(`${process.env.REACT_APP_BACKEND_URL}/api/quotes/${id}`, { withCredentials: true });
      fetchData();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || 'Failed to delete quote');
    }
  };

  const handleAddIncome = async () => {
    try {
      await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/income`, incomeForm, { withCredentials: true });
      setAddIncomeDialog(false);
      setIncomeForm({ amount: '', description: '', date: new Date().toISOString().split('T')[0] });
      fetchData();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || 'Failed to add income');
    }
  };

  const handleDeleteIncome = async (id) => {
    if (!window.confirm('Are you sure you want to delete this income entry?')) return;
    try {
      await axios.delete(`${process.env.REACT_APP_BACKEND_URL}/api/income/${id}`, { withCredentials: true });
      fetchData();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || 'Failed to delete income');
    }
  };

  const handleAddExpense = async () => {
    try {
      await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/expenses`, expenseForm, { withCredentials: true });
      setAddExpenseDialog(false);
      setExpenseForm({ amount: '', category: '', description: '', date: new Date().toISOString().split('T')[0] });
      fetchData();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || 'Failed to add expense');
    }
  };

  const handleDeleteExpense = async (id) => {
    if (!window.confirm('Are you sure you want to delete this expense?')) return;
    try {
      await axios.delete(`${process.env.REACT_APP_BACKEND_URL}/api/expenses/${id}`, { withCredentials: true });
      fetchData();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || 'Failed to delete expense');
    }
  };

  const statusColor = (status) => {
    if (status === 'completed') return 'bg-emerald-100 text-emerald-700 border-emerald-200';
    if (status === 'cancelled') return 'bg-red-100 text-red-700 border-red-200';
    return 'bg-amber-100 text-amber-700 border-amber-200';
  };

  return (
    <div className="p-8" data-testid="dashboard-content">
      {/* Page Header */}
      <div className="mb-8">
        <h1
          className="text-2xl font-bold text-[#121212]"
          style={{ fontFamily: 'Playfair Display, serif' }}
          data-testid="dashboard-heading"
        >
          Dashboard
        </h1>
        <p className="text-sm text-[#808080] mt-1">Overview of your business operations</p>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6" data-testid="dashboard-error-message">
          {error}
          <button onClick={() => setError('')} className="ml-3 underline text-sm">Dismiss</button>
        </div>
      )}

      {/* Overview Cards */}
      <div className="grid md:grid-cols-3 gap-5 mb-8">
        <div className="bg-white rounded-xl border border-[#EBEBEB] p-6 shadow-sm" data-testid="overview-income-card">
          <div className="flex items-center justify-between mb-3">
            <p className="text-sm font-medium text-[#808080]">Total Income</p>
            <div className="w-9 h-9 rounded-lg bg-emerald-50 flex items-center justify-center">
              <TrendingUp size={18} className="text-emerald-600" />
            </div>
          </div>
          <p className="text-2xl font-bold text-emerald-600" data-testid="overview-total-income">
            &euro;{overview.total_income.toFixed(2)}
          </p>
        </div>
        <div className="bg-white rounded-xl border border-[#EBEBEB] p-6 shadow-sm" data-testid="overview-expenses-card">
          <div className="flex items-center justify-between mb-3">
            <p className="text-sm font-medium text-[#808080]">Total Expenses</p>
            <div className="w-9 h-9 rounded-lg bg-red-50 flex items-center justify-center">
              <TrendingDown size={18} className="text-red-500" />
            </div>
          </div>
          <p className="text-2xl font-bold text-red-500" data-testid="overview-total-expenses">
            &euro;{overview.total_expenses.toFixed(2)}
          </p>
        </div>
        <div className="bg-white rounded-xl border border-[#EBEBEB] p-6 shadow-sm" data-testid="overview-profit-card">
          <div className="flex items-center justify-between mb-3">
            <p className="text-sm font-medium text-[#808080]">Net Profit</p>
            <div className="w-9 h-9 rounded-lg bg-[#C69C6D]/10 flex items-center justify-center">
              <DollarSign size={18} className="text-[#C69C6D]" />
            </div>
          </div>
          <p className="text-2xl font-bold text-[#121212]" data-testid="overview-net-profit">
            &euro;{overview.net_profit.toFixed(2)}
          </p>
        </div>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="quotes" className="space-y-5">
        <TabsList className="bg-white border border-[#EBEBEB] p-1 rounded-xl h-auto">
          <TabsTrigger value="quotes" className="rounded-lg px-5 py-2.5 text-sm data-[state=active]:bg-[#1A1A1A] data-[state=active]:text-white" data-testid="tab-quotes">
            <FileText size={15} className="mr-2" />Quotes
          </TabsTrigger>
          <TabsTrigger value="income" className="rounded-lg px-5 py-2.5 text-sm data-[state=active]:bg-[#1A1A1A] data-[state=active]:text-white" data-testid="tab-income">
            <TrendingUp size={15} className="mr-2" />Income
          </TabsTrigger>
          <TabsTrigger value="expenses" className="rounded-lg px-5 py-2.5 text-sm data-[state=active]:bg-[#1A1A1A] data-[state=active]:text-white" data-testid="tab-expenses">
            <TrendingDown size={15} className="mr-2" />Expenses
          </TabsTrigger>
        </TabsList>

        {/* Quotes Tab */}
        <TabsContent value="quotes" data-testid="quotes-section">
          <div className="bg-white rounded-xl border border-[#EBEBEB] shadow-sm overflow-hidden">
            <div className="px-6 py-4 border-b border-[#EBEBEB] flex items-center justify-between">
              <h3 className="font-semibold text-[#121212]">Quote Requests</h3>
              <span className="text-xs text-[#808080] bg-[#F5F5F0] px-3 py-1 rounded-full">{quotes.length} total</span>
            </div>
            <Table>
              <TableHeader>
                <TableRow className="bg-[#FAFAF8]">
                  <TableHead className="font-semibold text-xs uppercase tracking-wider">Product</TableHead>
                  <TableHead className="font-semibold text-xs uppercase tracking-wider">Customer</TableHead>
                  <TableHead className="font-semibold text-xs uppercase tracking-wider">Qty</TableHead>
                  <TableHead className="font-semibold text-xs uppercase tracking-wider">Files</TableHead>
                  <TableHead className="font-semibold text-xs uppercase tracking-wider">Status</TableHead>
                  <TableHead className="font-semibold text-xs uppercase tracking-wider">Price</TableHead>
                  <TableHead className="font-semibold text-xs uppercase tracking-wider">Date</TableHead>
                  <TableHead className="font-semibold text-xs uppercase tracking-wider">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {quotes.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center py-12 text-[#808080]">
                      No quotes yet. They will appear here when customers submit requests.
                    </TableCell>
                  </TableRow>
                )}
                {quotes.map((quote) => (
                  <TableRow key={quote.id} className="hover:bg-[#FAFAF8] transition-colors" data-testid={`quote-row-${quote.id}`}>
                    <TableCell className="font-medium text-[#121212]">{quote.product}</TableCell>
                    <TableCell>
                      <div>
                        <p className="text-sm font-medium">{quote.customer_name}</p>
                        <p className="text-xs text-[#808080]">{quote.customer_email}</p>
                      </div>
                    </TableCell>
                    <TableCell>{quote.quantity}</TableCell>
                    <TableCell>
                      {quote.files && quote.files.length > 0 ? (
                        <div className="flex items-center gap-1">
                          <Paperclip size={14} className="text-[#808080]" />
                          <span className="text-xs text-[#808080]">{quote.files.length}</span>
                        </div>
                      ) : (
                        <span className="text-xs text-[#C0C0C0]">-</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <span className={`inline-flex px-2.5 py-1 rounded-full text-xs font-medium border ${statusColor(quote.status)}`} data-testid={`quote-status-${quote.id}`}>
                        {quote.status}
                      </span>
                    </TableCell>
                    <TableCell className="font-medium">{quote.price ? `€${quote.price}` : <span className="text-[#C0C0C0]">-</span>}</TableCell>
                    <TableCell className="text-xs text-[#808080]">{new Date(quote.created_at).toLocaleDateString()}</TableCell>
                    <TableCell>
                      <div className="flex gap-1.5">
                        <button
                          onClick={() => {
                            setSelectedQuote(quote);
                            setQuoteStatus(quote.status);
                            setQuotePrice(quote.price || '');
                            setEditQuoteDialog(true);
                          }}
                          className="p-2 rounded-lg hover:bg-[#F5F5F0] text-[#808080] hover:text-[#121212] transition-colors"
                          data-testid={`edit-quote-${quote.id}`}
                        >
                          <Pencil size={15} />
                        </button>
                        {isAdmin && (
                          <button
                            onClick={() => handleDeleteQuote(quote.id)}
                            className="p-2 rounded-lg hover:bg-red-50 text-[#808080] hover:text-red-500 transition-colors"
                            data-testid={`delete-quote-${quote.id}`}
                          >
                            <Trash2 size={15} />
                          </button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </TabsContent>

        {/* Income Tab */}
        <TabsContent value="income" data-testid="income-section">
          <div className="bg-white rounded-xl border border-[#EBEBEB] shadow-sm overflow-hidden">
            <div className="px-6 py-4 border-b border-[#EBEBEB] flex items-center justify-between">
              <h3 className="font-semibold text-[#121212]">Income Records</h3>
              <Dialog open={addIncomeDialog} onOpenChange={setAddIncomeDialog}>
                <DialogTrigger asChild>
                  <Button size="sm" className="bg-[#1A1A1A] hover:bg-[#333] text-white rounded-lg" data-testid="add-income-button">
                    <Plus size={15} className="mr-1.5" />Add Income
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader><DialogTitle>Add Income Entry</DialogTitle></DialogHeader>
                  <div className="space-y-4 pt-2">
                    <div>
                      <Label>Amount (&euro;)</Label>
                      <Input type="number" step="0.01" value={incomeForm.amount} onChange={(e) => setIncomeForm({ ...incomeForm, amount: e.target.value })} className="mt-1.5" data-testid="income-amount-input" />
                    </div>
                    <div>
                      <Label>Description</Label>
                      <Input value={incomeForm.description} onChange={(e) => setIncomeForm({ ...incomeForm, description: e.target.value })} className="mt-1.5" data-testid="income-description-input" />
                    </div>
                    <div>
                      <Label>Date</Label>
                      <Input type="date" value={incomeForm.date} onChange={(e) => setIncomeForm({ ...incomeForm, date: e.target.value })} className="mt-1.5" data-testid="income-date-input" />
                    </div>
                    <Button onClick={handleAddIncome} className="w-full bg-[#1A1A1A] hover:bg-[#333] text-white" data-testid="submit-income-button">Add Income</Button>
                  </div>
                </DialogContent>
              </Dialog>
            </div>
            <Table>
              <TableHeader>
                <TableRow className="bg-[#FAFAF8]">
                  <TableHead className="font-semibold text-xs uppercase tracking-wider">Description</TableHead>
                  <TableHead className="font-semibold text-xs uppercase tracking-wider">Amount</TableHead>
                  <TableHead className="font-semibold text-xs uppercase tracking-wider">Date</TableHead>
                  <TableHead className="font-semibold text-xs uppercase tracking-wider">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {income.length === 0 && (
                  <TableRow><TableCell colSpan={4} className="text-center py-12 text-[#808080]">No income records yet.</TableCell></TableRow>
                )}
                {income.map((item) => (
                  <TableRow key={item.id} className="hover:bg-[#FAFAF8] transition-colors" data-testid={`income-row-${item.id}`}>
                    <TableCell className="font-medium">{item.description}</TableCell>
                    <TableCell className="font-semibold text-emerald-600">&euro;{item.amount}</TableCell>
                    <TableCell className="text-sm text-[#808080]">{new Date(item.date).toLocaleDateString()}</TableCell>
                    <TableCell>
                      {isAdmin && (
                        <button onClick={() => handleDeleteIncome(item.id)} className="p-2 rounded-lg hover:bg-red-50 text-[#808080] hover:text-red-500 transition-colors" data-testid={`delete-income-${item.id}`}>
                          <Trash2 size={15} />
                        </button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </TabsContent>

        {/* Expenses Tab */}
        <TabsContent value="expenses" data-testid="expenses-section">
          <div className="bg-white rounded-xl border border-[#EBEBEB] shadow-sm overflow-hidden">
            <div className="px-6 py-4 border-b border-[#EBEBEB] flex items-center justify-between">
              <h3 className="font-semibold text-[#121212]">Expense Records</h3>
              <Dialog open={addExpenseDialog} onOpenChange={setAddExpenseDialog}>
                <DialogTrigger asChild>
                  <Button size="sm" className="bg-[#1A1A1A] hover:bg-[#333] text-white rounded-lg" data-testid="add-expense-button">
                    <Plus size={15} className="mr-1.5" />Add Expense
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader><DialogTitle>Add Expense Entry</DialogTitle></DialogHeader>
                  <div className="space-y-4 pt-2">
                    <div>
                      <Label>Category</Label>
                      <Select value={expenseForm.category} onValueChange={(value) => setExpenseForm({ ...expenseForm, category: value })}>
                        <SelectTrigger className="mt-1.5" data-testid="expense-category-select"><SelectValue placeholder="Select category" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Rent">Rent</SelectItem>
                          <SelectItem value="Leasing">Leasing</SelectItem>
                          <SelectItem value="Paper">Paper</SelectItem>
                          <SelectItem value="Toner">Toner</SelectItem>
                          <SelectItem value="Other">Other</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Amount (&euro;)</Label>
                      <Input type="number" step="0.01" value={expenseForm.amount} onChange={(e) => setExpenseForm({ ...expenseForm, amount: e.target.value })} className="mt-1.5" data-testid="expense-amount-input" />
                    </div>
                    <div>
                      <Label>Description</Label>
                      <Input value={expenseForm.description} onChange={(e) => setExpenseForm({ ...expenseForm, description: e.target.value })} className="mt-1.5" data-testid="expense-description-input" />
                    </div>
                    <div>
                      <Label>Date</Label>
                      <Input type="date" value={expenseForm.date} onChange={(e) => setExpenseForm({ ...expenseForm, date: e.target.value })} className="mt-1.5" data-testid="expense-date-input" />
                    </div>
                    <Button onClick={handleAddExpense} className="w-full bg-[#1A1A1A] hover:bg-[#333] text-white" data-testid="submit-expense-button">Add Expense</Button>
                  </div>
                </DialogContent>
              </Dialog>
            </div>
            <Table>
              <TableHeader>
                <TableRow className="bg-[#FAFAF8]">
                  <TableHead className="font-semibold text-xs uppercase tracking-wider">Category</TableHead>
                  <TableHead className="font-semibold text-xs uppercase tracking-wider">Description</TableHead>
                  <TableHead className="font-semibold text-xs uppercase tracking-wider">Amount</TableHead>
                  <TableHead className="font-semibold text-xs uppercase tracking-wider">Date</TableHead>
                  <TableHead className="font-semibold text-xs uppercase tracking-wider">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {expenses.length === 0 && (
                  <TableRow><TableCell colSpan={5} className="text-center py-12 text-[#808080]">No expense records yet.</TableCell></TableRow>
                )}
                {expenses.map((item) => (
                  <TableRow key={item.id} className="hover:bg-[#FAFAF8] transition-colors" data-testid={`expense-row-${item.id}`}>
                    <TableCell>
                      <Badge variant="outline" className="text-xs font-medium">{item.category}</Badge>
                    </TableCell>
                    <TableCell className="font-medium">{item.description}</TableCell>
                    <TableCell className="font-semibold text-red-500">&euro;{item.amount}</TableCell>
                    <TableCell className="text-sm text-[#808080]">{new Date(item.date).toLocaleDateString()}</TableCell>
                    <TableCell>
                      {isAdmin && (
                        <button onClick={() => handleDeleteExpense(item.id)} className="p-2 rounded-lg hover:bg-red-50 text-[#808080] hover:text-red-500 transition-colors" data-testid={`delete-expense-${item.id}`}>
                          <Trash2 size={15} />
                        </button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </TabsContent>
      </Tabs>

      {/* Edit Quote Dialog */}
      <Dialog open={editQuoteDialog} onOpenChange={setEditQuoteDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-lg">Quote Details — {selectedQuote?.product}</DialogTitle>
          </DialogHeader>
          {selectedQuote && (
            <div className="space-y-5 pt-2">
              {/* Customer Info */}
              <div className="bg-[#FAFAF8] p-5 rounded-xl border border-[#EBEBEB]">
                <h4 className="font-semibold text-sm mb-3 text-[#808080] uppercase tracking-wider">Customer</h4>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div><span className="text-[#808080]">Name:</span> <span className="font-medium">{selectedQuote.customer_name}</span></div>
                  <div><span className="text-[#808080]">Email:</span> <span className="font-medium">{selectedQuote.customer_email}</span></div>
                  <div><span className="text-[#808080]">Phone:</span> <span className="font-medium">{selectedQuote.customer_phone}</span></div>
                  <div><span className="text-[#808080]">Quantity:</span> <span className="font-medium">{selectedQuote.quantity}</span></div>
                </div>
                {selectedQuote.notes && <p className="text-sm mt-3 pt-3 border-t border-[#EBEBEB]"><span className="text-[#808080]">Notes:</span> {selectedQuote.notes}</p>}
              </div>

              {/* Selected Options */}
              {selectedQuote.options && Object.keys(selectedQuote.options).length > 0 && (
                <div className="bg-[#FAFAF8] p-5 rounded-xl border border-[#EBEBEB]">
                  <h4 className="font-semibold text-sm mb-3 text-[#808080] uppercase tracking-wider">Product Options</h4>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    {Object.entries(selectedQuote.options).map(([key, value]) => (
                      <div key={key}>
                        <span className="text-[#808080] capitalize">{key.replace(/([A-Z])/g, ' $1')}:</span>{' '}
                        <span className="font-medium">{value}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Attachments */}
              {selectedQuote.files && selectedQuote.files.length > 0 && (
                <div className="bg-[#FAFAF8] p-5 rounded-xl border border-[#EBEBEB]">
                  <h4 className="font-semibold text-sm mb-3 text-[#808080] uppercase tracking-wider">
                    Attachments ({selectedQuote.files.length})
                  </h4>
                  <div className="space-y-2">
                    {selectedQuote.files.map((file, idx) => (
                      <div key={idx} className="flex items-center justify-between bg-white p-3 rounded-lg border border-[#EBEBEB]">
                        <div className="flex items-center gap-2">
                          <Paperclip size={14} className="text-[#808080]" />
                          <span className="text-sm font-medium truncate max-w-[240px]">{file.filename}</span>
                        </div>
                        <a
                          href={`${process.env.REACT_APP_BACKEND_URL}/api/files/${file.path}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-1 px-3 py-1.5 bg-[#1A1A1A] text-white text-xs rounded-lg hover:bg-[#333] transition-colors"
                          data-testid={`download-file-${idx}`}
                        >
                          <Download size={12} />Download
                        </a>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Edit Status & Price */}
              <div className="space-y-4 pt-4 border-t border-[#EBEBEB]">
                <div>
                  <Label className="text-sm font-medium">Status</Label>
                  <Select value={quoteStatus} onValueChange={setQuoteStatus}>
                    <SelectTrigger className="mt-1.5" data-testid="edit-quote-status"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="pending">Pending</SelectItem>
                      <SelectItem value="completed">Completed</SelectItem>
                      <SelectItem value="cancelled">Cancelled</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-sm font-medium">Price (&euro;)</Label>
                  <Input type="number" step="0.01" value={quotePrice} onChange={(e) => setQuotePrice(e.target.value)} className="mt-1.5" data-testid="edit-quote-price" />
                </div>
                <Button onClick={handleUpdateQuote} className="w-full bg-[#C69C6D] hover:bg-[#B38B5D] text-black font-semibold py-5" data-testid="update-quote-button">
                  Update Quote
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DashboardPage;
